import { AxiosError } from 'axios';
import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react';

import {
	Box,
	Button,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { RenterForm, RenterItem } from '@/entities/Renters';

import { renterApi } from '@/shared/api/renter';
import { chunk } from '@/shared/helpers/utils';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import { IRequestError } from '@/shared/types/app.types';
import { IRenter, TRenterRequestType } from '@/shared/types/renter.types';
import { SearchInput } from '@/shared/uikit/components/input';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

function Renters() {
	const queryClient = useQueryClient();
	const [renter, setRenter] = useState<IRenter | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activePage, setPage] = useState(1);
	const [searchValue, setSearchValue] = useState('');

	const { data: renters = [], isLoading } = useQuery({
		queryKey: ['renters'],
		queryFn: renterApi.getRenters,
	});

	const { mutateAsync: createRenter } = useMutation({
		mutationFn: renterApi.createRenter,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['renters'] });
			useShowNotifaction({ text: 'Арендатор создан', type: 'success' });
			setIsModalOpen(false);
			setRenter(null);
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data?.message ?? 'Не удалось создать арендатора',
				type: 'error',
			});
		},
	});

	const { mutateAsync: updateRenter } = useMutation({
		mutationFn: renterApi.updateRenter,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['renters'] });
			useShowNotifaction({ text: 'Арендатор изменён', type: 'success' });
			setIsModalOpen(false);
			setRenter(null);
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data?.message ?? 'Не удалось изменить арендатора',
				type: 'error',
			});
		},
	});

	const { mutateAsync: deleteRenter } = useMutation({
		mutationFn: renterApi.deleteRenter,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['renters'] });
			useShowNotifaction({ text: 'Арендатор удалён', type: 'success' });
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: error.response?.data?.message ?? 'Не удалось удалить арендатора',
				type: 'error',
			});
		},
	});

	const handleEdit = (item: IRenter) => {
		setRenter(item);
		setIsModalOpen(true);
	};

	const handleCreate = async (body: TRenterRequestType) => {
		await createRenter(body);
	};

	const handleUpdate = async (body: TRenterRequestType, id: number) => {
		await updateRenter({ id, payload: body });
	};

	const handleClose = () => {
		setRenter(null);
		setIsModalOpen(false);
	};

	const handleDelete = useCallback(
		async (id: number) => {
			await deleteRenter(id);
		},
		[deleteRenter],
	);

	const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.currentTarget.value);
		setPage(1);
	}, []);

	const filteredRenters = useMemo(() => {
		const query = searchValue.toLowerCase().trim();

		if (!query) {
			return renters;
		}

		return renters.filter((item) => item.renter_name.toLowerCase().includes(query));
	}, [renters, searchValue]);

	const chunked = chunk(filteredRenters, 10);

	const rows = chunked[activePage - 1]?.map((elem) => (
		<RenterItem key={elem.id} renter={elem} handleEdit={handleEdit} handleDelete={handleDelete} />
	));

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Арендаторы', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<Box
					pos="relative"
					style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
				>
					<LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
					<Group justify="space-between">
						<Button
							variant="filled"
							radius="lg"
							onClick={() => {
								setRenter(null);
								setIsModalOpen(true);
							}}
						>
							<Text fw={500}>Создать</Text>
						</Button>
						<SearchInput
							w={400}
							value={searchValue}
							onChange={handleSearch}
							placeholder="Поиск по арендаторам"
						/>
					</Group>
					<ScrollArea h={'100vh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
						<Table verticalSpacing="md" stickyHeader>
							<Table.Thead>
								<Table.Tr>
									<Table.Th>
										<Text size="md" c="dimmed">
											Название
										</Text>
									</Table.Th>
									<Table.Th ta="right" style={{ width: '1%', whiteSpace: 'nowrap' }}>
										<Text size="md" c="dimmed">
											Действия
										</Text>
									</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>{rows}</Table.Tbody>
						</Table>
					</ScrollArea>
					{chunked.length > 1 && (
						<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
					)}
				</Box>
			</LayoutRow>
			<RenterForm
				isOpen={isModalOpen}
				renter={renter}
				onCreate={handleCreate}
				onUpdate={handleUpdate}
				handleClose={handleClose}
			/>
		</>
	);
}

export default memo(Renters);
